import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactECharts from 'echarts-for-react';
import AppLayout from '../components/layout/AppLayout';
import Seo from '../components/Seo';
import { useWallet } from '../context/WalletContext';
import { getUserPortfolio, getVaultData } from '../lib/api';
import { useConnectModal } from 'thirdweb/react';
import { thirdwebClient, supportedWallets } from '../lib/thirdweb';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

/* Tiny SVG sparkline — no deps, pure math */
function Sparkline({ data, positive = true, width = 120, height = 36 }) {
  if (!data || data.length < 2) return null;
  const vals = data.map(d => d.v);
  const mn = Math.min(...vals), mx = Math.max(...vals);
  const range = mx - mn || 1;
  const pad = 2;
  const pts = data.map((d, i) => {
    const x = pad + (i / (data.length - 1)) * (width - pad * 2);
    const y = pad + (1 - (d.v - mn) / range) * (height - pad * 2);
    return `${x},${y}`;
  });
  const color = positive ? '#00D4A1' : '#EF4444';
  const fillId = `spk-${positive ? 'p' : 'n'}`;
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} fill="none" style={{ display: 'block' }}>
      <defs>
        <linearGradient id={fillId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.18" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

function fmtUsd(v) {
  if (v == null) return '—';
  if (v === 0) return '$0.00';
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
}
function fmtPct(v) { return v != null ? `${v >= 0 ? '+' : ''}${v.toFixed(2)}%` : '—'; }
function fmtAum(v) {
  if (!v) return '$0';
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(1)}K`;
  return `$${v.toFixed(0)}`;
}

const POSITIONS_PER_PAGE = 5;

const TF_KEYS = ['1D', '1W', '1M', 'ALL'];

// Demo chart — flat indigo line shown when wallet not connected
const DEMO_CHART_DATA = (() => {
  const now = Date.now();
  const span = 30 * 24 * 3600 * 1000;
  return [0, 0.25, 0.5, 0.75, 1].map(f => ({ t: now - span + f * span, v: 10000 }));
})();

// Placeholder donut data — single neutral ring
const PLACEHOLDER_DONUT = [{ name: '—', value: 1, itemStyle: { color: 'rgba(120,120,120,0.18)' } }];

/* Hero chart — full-bleed, crosshair on hover */
function HeroChart({ data, neutral = false, locked = false, mobile = false }) {
  // locked = demo mode: indigo color, flat line, no tooltip
  const color = neutral ? 'rgba(255,255,255,0.80)' : '#6366f1';

  // Pad y-axis so a flat/low-range line sits centred vertically
  const yAxisExtra = (neutral || locked) ? (() => {
    const vals = data.map(d => d.v);
    const mn = Math.min(...vals), mx = Math.max(...vals);
    const amp = Math.max((mx - mn), mn * 0.04) || 1;
    return { min: mn - amp * 1.5, max: mx + amp * 1.5 };
  })() : {};

  const option = useMemo(() => ({
    animation: true,
    animationDuration: 900,
    animationEasing: 'cubicOut',
    grid: { top: mobile ? 52 : 110, bottom: mobile ? 88 : 12, left: 0, right: 0 },
    xAxis: {
      type: 'time',
      axisLine: { show: false }, axisTick: { show: false },
      splitLine: { show: false }, axisLabel: { show: false },
    },
    yAxis: {
      type: 'value', scale: true,
      ...yAxisExtra,
      splitLine: { show: false }, axisLabel: { show: false },
      axisLine: { show: false }, axisTick: { show: false },
    },
    series: [{
      type: 'line',
      data: data.map(d => [d.t, d.v]),
      smooth: false,
      symbolSize: (val, params) => (!neutral && !locked && params.dataIndex === data.length - 1) ? 7 : 0,
      showSymbol: !neutral && !locked, symbol: 'circle',
      lineStyle: { color, width: 2.5, shadowColor: neutral ? 'rgba(255,255,255,0.3)' : 'rgba(99,102,241,0.4)', shadowBlur: neutral ? 6 : 14 },
      itemStyle: { color, borderColor: 'rgba(0,0,0,0.4)', borderWidth: 2 },
      areaStyle: {
        color: {
          type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
          colorStops: neutral
            ? [
                { offset: 0,    color: 'rgba(255,255,255,0.18)' },
                { offset: 0.7,  color: 'rgba(255,255,255,0.05)' },
                { offset: 1,    color: 'rgba(0,0,0,0)' },
              ]
            : [
                { offset: 0,    color: 'rgba(99,102,241,0.55)' },
                { offset: 0.5,  color: 'rgba(99,102,241,0.20)' },
                { offset: 1,    color: 'rgba(99,102,241,0)' },
              ],
        },
      },
    }],
    tooltip: (neutral || locked) ? { show: false } : {
      trigger: 'axis',
      axisPointer: {
        type: 'cross',
        lineStyle:  { color: 'rgba(255,255,255,0.22)', type: 'solid', width: 1 },
        crossStyle: { color: 'rgba(255,255,255,0.22)', type: 'dashed', width: 1 },
        label: { show: false },
      },
      backgroundColor: 'rgba(10,10,10,0.95)',
      borderColor: 'rgba(255,255,255,0.10)',
      borderWidth: 1, padding: [14, 20],
      textStyle: { fontFamily: 'JetBrains Mono', fontSize: 12, color: '#fff' },
      formatter: (params) => {
        const d = new Date(params[0].value[0]);
        const rangeMs = data.length >= 2 ? data[data.length-1].t - data[0].t : Infinity;
        const dateStr = rangeMs < 2 * 86400000
          ? d.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }).toUpperCase()
          : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).toUpperCase();
        const val = params[0].value[1];
        const timeStr = `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
        const fullDateStr = rangeMs < 2 * 86400000 ? dateStr : `${dateStr} · ${timeStr}`;
        return `<div style="font-size:9px;color:rgba(255,255,255,0.5);margin-bottom:8px;letter-spacing:0.1em">${fullDateStr}</div><div style="font-weight:700;font-size:22px;letter-spacing:-0.03em">${fmtUsd(val)}</div>`;
      },
    },
  }), [data, neutral, locked, mobile, color]);

  if (!data || data.length < 2) return null;
  return <ReactECharts option={option} style={{ width: '100%', height: '100%' }} opts={{ renderer: 'canvas' }} />;
}

/* Stacked segmented bar — horizontal, 1 row */
function CapitalStackedBar({ deposited, withdrawn, pnl }) {
  const pnlPositive = pnl >= 0;
  const pnlColor = pnlPositive ? '#22C55E' : '#EF4444';
  // Active = what's still deployed
  const active = Math.max(deposited - withdrawn, 0);
  // If loss, it visually eats into active capital
  const activeFinal = pnlPositive ? active : Math.max(active + pnl, 0);
  const lossAmt     = pnlPositive ? 0 : Math.min(Math.abs(pnl), active);
  const gainAmt     = pnlPositive ? pnl : 0;

  // Series: [current active] [loss (if any)] [withdrawn] [gain (if any)]
  // All on the same horizontal bar (stack)
  const series = [
    { name: 'Actif',    value: activeFinal, color: '#5B5FFF',                   radius: [4,4,4,4] },
    { name: 'Perte',    value: lossAmt,     color: pnlColor,                    radius: [0,0,0,0] },
    { name: 'Retiré',  value: withdrawn,   color: 'rgba(120,120,120,0.40)',    radius: withdrawn > 0 ? [0,0,0,0] : [0,0,0,0] },
    { name: 'Gain',     value: gainAmt,     color: pnlColor,                    radius: [4,4,4,4] },
  ];

  // Compute border-radius per segment considering which are non-zero
  const nonZero = series.filter(s => s.value > 0);
  const first = nonZero[0];
  const last  = nonZero[nonZero.length - 1];

  const option = useMemo(() => ({
    animation: true,
    animationDuration: 900,
    animationEasing: 'cubicOut',
    grid: { top: 0, bottom: 0, left: 0, right: 0, containLabel: false },
    xAxis: { type: 'value', show: false, min: 0, max: series.reduce((s, x) => s + x.value, 0) },
    yAxis: { type: 'category', show: false, data: [''] },
    tooltip: {
      trigger: 'item',
      backgroundColor: 'rgba(10,10,10,0.94)',
      borderColor: 'rgba(255,255,255,0.10)',
      borderWidth: 1, padding: [10,16],
      textStyle: { fontFamily: 'Montserrat', fontSize: 12, color: '#fff' },
      formatter: (p) => {
        if (p.value === 0) return '';
        const sign = p.seriesName === 'Gain' ? '+' : (p.seriesName === 'Perte' ? '-' : '');
        return `<div style="font-size:9px;color:#888;margin-bottom:4px;text-transform:uppercase;letter-spacing:.08em">${p.seriesName}</div><div style="font-weight:700;font-size:16px;color:${p.color}">${sign}${fmtUsd(p.value)}</div>`;
      },
    },
    series: series.map((s, i) => ({
      name: s.name,
      type: 'bar',
      stack: 'cap',
      barWidth: 44,
      data: [s.value === 0 ? { value: 0, itemStyle: { color: 'transparent' } } : {
        value: s.value,
        itemStyle: {
          color: s.color,
          borderRadius: [
            s === first ? 4 : 0, s === last ? 4 : 0,
            s === last ? 4 : 0,  s === first ? 4 : 0,
          ],
          shadowColor: (s.name === 'Gain' || s.name === 'Actif') ? `${s.color}44` : 'transparent',
          shadowBlur: (s.name === 'Gain' || s.name === 'Actif') ? 6 : 0,
        },
      }],
    })),
  }), [deposited, withdrawn, pnl]);

  return <ReactECharts option={option} style={{ width: '100%', height: '100%' }} opts={{ renderer: 'canvas' }} />;
}

/* Daily PnL ECharts bar chart */
function DailyPnLChart({ bars, locale = 'en' }) {
  const maxAbs = Math.max(...bars.map(b => Math.abs(b.delta)), 0.01);
  const loc = locale === 'fr' ? 'fr-FR' : 'en-US';

  const option = useMemo(() => ({
    animation: true,
    animationDuration: 700,
    animationEasing: 'cubicOut',
    grid: { top: 8, bottom: 28, left: 48, right: 8, containLabel: false },
    xAxis: {
      type: 'category',
      data: bars.map(b => {
        const d = new Date(b.day + 'T12:00:00');
        return d.toLocaleDateString(loc, { weekday: 'short' }).toUpperCase().slice(0, 3);
      }),
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: {
        fontSize: 10, color: 'rgba(140,140,140,0.9)',
        fontFamily: 'Montserrat', fontWeight: '700',
        interval: 0,
      },
    },
    yAxis: {
      type: 'value', scale: false,
      splitNumber: 4,
      axisLine: { show: false }, axisTick: { show: false },
      axisLabel: {
        show: true, fontSize: 9, color: 'rgba(140,140,140,0.7)',
        fontFamily: 'Montserrat', fontWeight: '600',
        formatter: v => v === 0 ? '$0' : (v >= 0 ? `+$${Math.round(v)}` : `-$${Math.round(Math.abs(v))}`),
        align: 'left',
        margin: 48,
      },
      splitLine: {
        show: true,
        lineStyle: { color: 'rgba(255,255,255,0.06)', width: 1, type: 'dashed' },
      },
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'none' },
      backgroundColor: 'rgba(10,10,10,0.94)',
      borderColor: 'rgba(255,255,255,0.10)',
      borderWidth: 1,
      padding: [10, 16],
      textStyle: { fontFamily: 'Montserrat', fontSize: 12, color: '#fff' },
      formatter: (params) => {
        const b = bars[params[0].dataIndex];
        const sign = b.delta >= 0 ? '+' : '';
        const color = b.delta >= 0 ? '#22C55E' : '#EF4444';
        const label = new Date(b.day + 'T12:00:00').toLocaleDateString(loc, { weekday: 'long', month: 'short', day: 'numeric' });
        return `<div style="font-size:9px;color:#888;margin-bottom:5px;letter-spacing:.08em;text-transform:uppercase">${label}</div><div style="font-weight:700;font-size:17px;color:${color}">${sign}${fmtUsd(b.delta)}</div>`;
      },
    },
    series: [{
      type: 'bar',
      barCategoryGap: '28%',
      data: bars.map(b => {
        const isPos = b.delta >= 0;
        return {
          value: b.delta,
          itemStyle: {
            borderRadius: isPos ? [5, 5, 0, 0] : [0, 0, 5, 5],
            color: isPos ? '#22C55E' : '#EF4444',
          },
        };
      }),
    }],
  }), [bars, maxAbs, loc]);

  return <ReactECharts option={option} style={{ width: '100%', height: '100%' }} opts={{ renderer: 'canvas' }} />;
}

/* Donut only (chart) — no tooltip, fires onHover(index|null) */
// ── Token helpers ──────────────────────────────────────────────────────────────
// Get display token — vault symbol from DB (e.g. "bG12"), fallback to name
function positionToken(pos) {
  return pos.symbol ?? pos.name.split(' ')[0];
}
function getTokenColor(token) {
  const t = token.toUpperCase();
  if (/BG12|G12/.test(t)) return '#F97316';  // G12 orange — bG12
  if (/BTC|WBTC/.test(t)) return '#F97316';  // G12 orange — BTC
  if (/ETH|WETH|STETH/.test(t)) return '#818CF8'; // indigo — ETH
  if (/USDC|USDT|DAI/.test(t)) return '#38BDF8'; // sky blue — stable
  if (/MATIC/.test(t)) return '#8B5CF6';
  if (/ARB/.test(t)) return '#60A5FA';
  if (/OP/.test(t)) return '#F43F5E';
  const FALLBACK = ['#5B5FFF','#6366F1','#A5B4FC','#4338CA','#3730A3'];
  return FALLBACK[0];
}
const STRATEGY_COLORS = ['#6366F1','#F97316','#22C55E','#38BDF8','#F43F5E','#8B5CF6','#FBBF24','#14B8A6'];

function HalfDonutChart({ positions, onHover, placeholder = false, colors = [], chartRef, fullDonut = false, hoveredIdx = null }) {
  const real = placeholder
    ? PLACEHOLDER_DONUT
    : positions.map((p, i) => ({
        value: p.value, name: p.name,
        itemStyle: { color: colors[i] ?? STRATEGY_COLORS[i % STRATEGY_COLORS.length] },
      }));
  const total = real.reduce((s, d) => s + d.value, 0);

  const hoveredPct = hoveredIdx != null && real[hoveredIdx] && total > 0
    ? (real[hoveredIdx].value / total * 100).toFixed(0)
    : null;
  const hoveredColor = hoveredIdx != null && real[hoveredIdx]
    ? (colors[hoveredIdx] ?? STRATEGY_COLORS[hoveredIdx % STRATEGY_COLORS.length])
    : '#fff';

  const option = useMemo(() => ({
    animation: true, animationDuration: 900, animationEasing: 'cubicOut',
    tooltip: { show: false },
    graphic: hoveredPct != null ? [{
      type: 'text',
      left: 'center',
      top: fullDonut ? 'center' : '38%',
      style: {
        text: `${hoveredPct}%`,
        fill: hoveredColor,
        fontSize: 20,
        fontWeight: 700,
        fontFamily: 'Montserrat',
      },
    }] : [],
    series: [{
      type: 'pie',
      radius: ['52%', '80%'],
      center: fullDonut ? ['50%', '50%'] : ['50%', '100%'],
      startAngle: fullDonut ? 90 : 180,
      data: fullDonut
        ? real
        : [
            ...real,
            { value: total, name: '', itemStyle: { color: 'transparent' }, emphasis: { disabled: true }, tooltip: { show: false } },
          ],
      avoidLabelOverlap: false,
      label: { show: false },
      emphasis: placeholder ? { disabled: true } : {
        label: { show: false },
        itemStyle: { shadowBlur: 20, shadowColor: 'rgba(0,0,0,0.4)', scaleSize: 4 },
      },
      itemStyle: { borderColor: 'transparent', borderWidth: 4 },
    }],
  }), [real, total, placeholder, fullDonut, hoveredPct, hoveredColor]);

  const events = useMemo(() => placeholder ? {} : ({
    mouseover: (p) => { if (p.name) onHover?.(p.dataIndex); },
    mouseout:  ()  => onHover?.(null),
  }), [onHover, placeholder]);

  return <ReactECharts ref={chartRef} option={option} onEvents={events} style={{ width: '100%', height: '100%' }} opts={{ renderer: 'canvas' }} />;
}

export default function Dashboard() {
  const { t } = useTranslation('dashboard');
  const { t: tc } = useTranslation('common');
  const { walletAddress, walletReady } = useWallet();
  const navigate = useNavigate();
  const { connect } = useConnectModal();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 600);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 600);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  const [portfolio, setPortfolio] = useState(null);
  const [portfolioWeekly, setPortfolioWeekly] = useState(null);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState('1D');
  const [hoveredDonutIdx, setHoveredDonutIdx] = useState(null);
  const halfDonutRef = useRef(null);
  const [positionPage, setPositionPage] = useState(0);
  const [openPositionIdx, setOpenPositionIdx] = useState(0);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [portfolioResult, weeklyResult] = walletAddress
        ? await Promise.all([
            getUserPortfolio(walletAddress, timeframe),
            getUserPortfolio(walletAddress, '1W'),
          ])
        : [null, null];
      setPortfolio(portfolioResult);
      setPortfolioWeekly(weeklyResult);
      if (portfolioResult?.positions?.length) {
        const withData = await Promise.all(
          portfolioResult.positions.map(async (pos) => {
            try {
              const vData = await getVaultData(pos.vault_id, walletAddress, '1M');
              const currentVal = pos.current_value_usdc ?? 0;
              const shares = pos.current_shares ?? 0;
              const pnl = pos.total_pnl_usdc ?? null;
              const perfAll = pos.pnl_percentage ?? null;
              const costBasis = pos.net_invested_usdc ?? 0;
              const isClosed = shares < 0.0001 && currentVal < 0.01;
              return {
                vaultId: pos.vault_id,
                name: vData?.vault?.name ?? 'Vault',
                symbol: vData?.vault?.symbol ?? null,
                exposure: vData?.vault?.exposure ?? [],
                providerLogo: vData?.vault?.provider_logo_url ?? '/SVG-05.svg',
                value: currentVal,
                shares,
                avgEntry: pos.avg_entry_price ?? null,
                perfAll,
                pnl,
                costBasis,
                status: isClosed ? 'closed' : 'active',
                totalWithdrawn: pos.total_withdrawn_usdc ?? null,
              };
            } catch {
              const fallbackShares = pos.current_shares ?? 0;
              const fallbackVal = pos.current_value_usdc ?? 0;
              return {
                vaultId: pos.vault_id, name: 'Vault', providerLogo: '/SVG-05.svg',
                value: fallbackVal, shares: fallbackShares,
                perfAll: null, pnl: null, costBasis: 0,
                status: (fallbackShares < 0.0001 && fallbackVal < 0.01) ? 'closed' : 'active',
                totalWithdrawn: pos.total_withdrawn_usdc ?? null,
              };
            }
          })
        );
        // Include all positions (active + closed), active first
        setPositions(withData.sort((a, b) => (a.status === 'closed') - (b.status === 'closed')));
      } else {
        setPositions([]);
      }
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  }, [walletAddress, timeframe]);

  useEffect(() => { if (walletReady) loadData(); }, [walletAddress, walletReady, loadData]);

  // When wallet disconnects, reset state
  useEffect(() => {
    if (!walletAddress) { setPortfolio(null); setPositions([]); setLoading(false); }
  }, [walletAddress]);

  // Reset position page when positions change
  useEffect(() => { setPositionPage(0); }, [positions.length]);

  // Highlight/downplay half-donut segment on legend hover
  useEffect(() => {
    const chart = halfDonutRef.current?.getEchartsInstance?.();
    if (!chart) return;
    if (hoveredDonutIdx !== null) {
      chart.dispatchAction({ type: 'highlight', seriesIndex: 0, dataIndex: hoveredDonutIdx });
    } else {
      chart.dispatchAction({ type: 'downplay', seriesIndex: 0 });
    }
  }, [hoveredDonutIdx]);

  const totalValue = portfolio?.total_value_usdc ?? 0;
  const pct1d      = portfolio?.change_1d_pct ?? null;
  const pct7d      = portfolio?.change_7d_pct    ?? portfolio?.perf_7d_pct  ?? null;
  const pct30d     = portfolio?.perf_30d_pct      ?? null;
  const pct3m      = portfolio?.perf_3m_pct       ?? null;
  const pctAll     = portfolio?.perf_all_pct      ?? portfolio?.total_return_pct ?? null;
  const totalPnl   = positions.length > 0
    ? positions.reduce((sum, p) => sum + (p.pnl ?? 0), 0)
    : (portfolio?.total_pnl_usdc ?? portfolio?.net_profit_usdc ?? null);
  const totalInvested = positions.length > 0
    ? positions.reduce((sum, p) => sum + (p.costBasis ?? 0), 0)
    : null;
  const chartData = useMemo(() => {
    const pts = (portfolio?.chart_data ?? [])
      .map(p => ({ t: new Date(p.timestamp).getTime(), v: p.value }))
      .sort((a, b) => a.t - b.t);
    // Append live value as last point if it's more recent than the last snapshot
    if (pts.length > 0 && totalValue > 0) {
      const now = Date.now();
      const lastT = pts[pts.length - 1].t;
      if (now - lastT > 60_000) pts.push({ t: now, v: totalValue });
    }
    // Fill gaps: if two consecutive points are >36h apart, interpolate intermediate points
    // (handles missing daily snapshots e.g. over long weekends)
    const GAP_THRESHOLD = 36 * 3600 * 1000; // 36h
    const filled = [];
    for (let i = 0; i < pts.length; i++) {
      filled.push(pts[i]);
      if (i < pts.length - 1) {
        const gap = pts[i + 1].t - pts[i].t;
        if (gap > GAP_THRESHOLD) {
          // Insert one midpoint per missing day
          const steps = Math.round(gap / (24 * 3600 * 1000));
          for (let s = 1; s < steps; s++) {
            const frac = s / steps;
            filled.push({
              t: pts[i].t + frac * gap,
              v: pts[i].v + frac * (pts[i + 1].v - pts[i].v),
            });
          }
        }
      }
    }
    return filled;
  }, [portfolio?.chart_data, totalValue]);

  const perfForTf = useMemo(() => {
    // Try API-returned perf for the selected timeframe first
    const apiVal = timeframe === '1D' ? pct1d : timeframe === '1W' ? pct7d : timeframe === '1M' ? pct30d : pctAll;
    if (apiVal != null) return apiVal;
    // Fallback: compute from chart data (first visible point → current portfolio value)
    if (chartData.length >= 2 && totalValue > 0) {
      const first = chartData[0].v;
      if (first > 0) return (totalValue - first) / first * 100;
    }
    // For 1D / 1W: use portfolioWeekly chart (always loaded) as reference source
    if ((timeframe === '1D' || timeframe === '1W') && totalValue > 0) {
      const wPts = (portfolioWeekly?.chart_data ?? [])
        .map(p => ({ t: new Date(p.timestamp).getTime(), v: p.value }))
        .sort((a, b) => a.t - b.t);
      if (wPts.length >= 2) {
        const cutoff = timeframe === '1D' ? Date.now() - 24 * 3600 * 1000 : wPts[0].t;
        // Find the first point at or after the cutoff (or the earliest available)
        const refPt = wPts.find(p => p.t >= cutoff) ?? wPts[0];
        if (refPt.v > 0) return (totalValue - refPt.v) / refPt.v * 100;
      }
    }
    return null;
  }, [timeframe, pct1d, pct7d, pct30d, pctAll, chartData, totalValue, portfolioWeekly]);
  // Monochromatic indigo palette for donut

  // Daily PnL bars — computed from 1W chart data (always 7 days regardless of timeframe selector)
  const dailyBars = useMemo(() => {
    const allPts = (portfolioWeekly?.chart_data ?? []).map(p => ({ t: new Date(p.timestamp).getTime(), v: p.value }));
    if (allPts.length < 2) return [];
    const byDay = new Map();
    for (const pt of allPts) {
      const day = new Date(pt.t).toISOString().slice(0, 10);
      if (!byDay.has(day)) byDay.set(day, { first: pt.v, last: pt.v });
      else byDay.get(day).last = pt.v;
    }
    return Array.from(byDay.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-7) // last 7 days
      .map(([day, { first, last }]) => ({ day, delta: last - first }));
  }, [portfolioWeekly?.chart_data]);

  const isDemo = !walletAddress;
  const chartToShow = isDemo ? DEMO_CHART_DATA : chartData;

  async function handleConnect() {
    try {
      await connect({ client: thirdwebClient, wallets: supportedWallets, showThirdwebBranding: false, title: 'Connect to G12 Labs' });
    } catch (err) {
      if (err?.message !== 'Connection closed') console.error(err);
    }
  }

  // Capital arc helpers
  const dep = portfolio?.total_deposited_usdc ?? 0;
  const wit = portfolio?.total_withdrawn_usdc ?? 0;
  const activePct = dep > 0 ? Math.min(100, Math.max(0, ((dep - wit) / dep) * 100)) : 100;
  const R = 62, cx = 80, cy = 76;
  const arcAngle = Math.PI * (activePct / 100);
  const x1 = cx + R * Math.cos(Math.PI);
  const y1 = cy + R * Math.sin(Math.PI);
  const x2 = cx + R * Math.cos(Math.PI + arcAngle);
  const y2 = cy + R * Math.sin(Math.PI + arcAngle);
  const largeArc = arcAngle > Math.PI ? 1 : 0;

  const totalPages = Math.ceil(positions.length / POSITIONS_PER_PAGE);
  const pagedPositions = positions.slice(positionPage * POSITIONS_PER_PAGE, (positionPage + 1) * POSITIONS_PER_PAGE);

  return (
    <AppLayout>
      <Seo
        title="Dashboard | G12 Labs"
        description="Your G12 Labs portfolio dashboard."
        path="/dashboard"
        noindex
      />
      <div className="dash-container">

        {/* ── Hero + Row 2 — locked when no wallet ── */}
        <div className={isDemo ? 'dash-locked-wrap' : ''}>
          {isDemo && (
            <div className="dash-locked-overlay">
              <div className="dash-locked-inner">
                <div className="dash-locked-icon"><i className="ph-bold ph-lock-simple" /></div>
                <p className="dash-locked-text">{t('connectToView')}</p>
                <button className="dash-locked-btn" onClick={handleConnect}>
                  <i className="ph-bold ph-wallet" /> {tc('wallet.connectWallet')}
                </button>
              </div>
            </div>
          )}
          <div className={`dash-hero-bento-group${isDemo ? ' dash-locked-content' : ''}`}>

            {/* ── Hero chart ── */}
            <div className="dash-hero">
              <div className="dash-chart-bg">
                {loading && !isDemo
                  ? <div className="skel-block" style={{ width: '100%', height: '100%' }} />
                  : chartToShow.length >= 2
                    ? <HeroChart data={chartToShow} locked={isDemo} mobile={isMobile} />
                    : <div className="dash-chart-empty-bg" />
                }
              </div>
              <div className="dash-hero-top-bar">
                <div className="dash-hero-val-block">
                  <span className="dash-hero-label">{t('portfolioValue')}</span>
                  <div className="dash-hero-amount-row">
                    {loading && !isDemo
                      ? <span className="skel-block" style={{ width: 240, height: 62, borderRadius: 6, display: 'block' }} />
                      : <h1 className="dash-hero-value">{isDemo ? '$0.00' : fmtUsd(totalValue)}</h1>
                    }
                    <span className={`dash-hero-perf-inline${isDemo ? ' positive' : (!loading && perfForTf != null ? (perfForTf >= 0 ? ' positive' : ' negative') : '')}`}>
                      {isDemo ? '+0.00%' : loading ? '···' : fmtPct(perfForTf)}
                    </span>
                  </div>
                </div>
                <div className="dash-tf-tabs">
                  {TF_KEYS.map(key => (
                    <button key={key} className={`dash-tf-btn ${timeframe === key ? 'active' : ''}`} onClick={() => setTimeframe(key)}>
                      {key === 'ALL' ? t('tfAll') : key}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* ── Row 2: PnL + Donut ── */}
            <div className="dash-row-2">

              {/* Daily gains card */}
              <div className="dash-bento-pnl">
                <div className="dash-pnl-header">
                  <span className="dash-bento-card-title">{t('dailyGains')}</span>
                </div>
                <div className="dash-pnl-chart-wrap">
                  {!isDemo && dailyBars.length > 0
                    ? <DailyPnLChart bars={dailyBars} locale={i18n.language} />
                    : <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                        <span style={{ fontSize: 12, color: 'var(--text-2)' }}>{t('noData')}</span>
                      </div>
                  }
                </div>
              </div>

              {/* Capital Invested card */}
              {(() => {
                const roi = totalInvested > 0 && totalPnl != null ? (totalPnl / totalInvested) * 100 : null;
                const roiPositive = roi != null && roi >= 0;
                const pnlColor = roiPositive ? '#22C55E' : '#EF4444';
                const hasSufficientData = !isDemo && dep > 0 && totalPnl != null;
                const swatches = [
                  { label: 'Actif',   color: '#5B5FFF' },
                  ...(wit > 0           ? [{ label: 'Retiré', color: 'rgba(120,120,120,0.7)' }] : []),
                  ...(totalPnl > 0      ? [{ label: 'Gain',   color: '#22C55E' }] : []),
                  ...(totalPnl != null && totalPnl < 0 ? [{ label: 'Perte', color: '#EF4444' }] : []),
                ];
                return (
                  <div className="dash-bento-donut">

                    {/* Card title */}
                    <span className="dash-bento-card-title">Capital</span>

                    {/* Top row: 3 KPIs */}
                    <div className="dash-cap-kpi-row">
                      <div className="dash-cap-kpi">
                        <span className="dash-kpi-label">{t('invested')}</span>
                        <span className="dash-cap-kpi-val">{isDemo ? '—' : fmtUsd(totalInvested)}</span>
                      </div>
                      <div className="dash-cap-kpi dash-cap-kpi--center">
                        <span className="dash-kpi-label">{t('withdrawn')}</span>
                        <span className="dash-cap-kpi-val">{isDemo ? '—' : fmtUsd(wit)}</span>
                      </div>
                      <div className="dash-cap-kpi dash-cap-kpi--right">
                        <span className="dash-kpi-label">PnL net</span>
                        <span className="dash-cap-kpi-val" style={{ color: pnlColor }}>
                          {isDemo ? '—' : `${roiPositive ? '+' : ''}${fmtUsd(totalPnl)}`}
                        </span>
                      </div>
                    </div>

                    {/* ROI — bottom right */}
                    {!isDemo && roi != null && (
                      <div className={`dash-cap-roi-card${roiPositive ? '' : ' negative'}`}>
                        <span className="dash-cap-roi-lbl">ROI</span>
                        <span className="dash-cap-roi-val" style={{ color: roiPositive ? '#22C55E' : '#EF4444' }}>
                          {roiPositive ? '+' : ''}{roi.toFixed(2)}%
                        </span>
                      </div>
                    )}

                    {/* Stacked bar + legend */}
                    {hasSufficientData && (
                      <>
                        <div className="dash-waterfall-wrap">
                          <CapitalStackedBar deposited={dep} withdrawn={wit} pnl={totalPnl} />
                        </div>
                        <div className="dash-cap-swatches">
                          {swatches.map(s => (
                            <div key={s.label} className="dash-cap-swatch">
                              <span style={{ width: 8, height: 8, borderRadius: 2, background: s.color, flexShrink: 0, display: 'inline-block' }} />
                              <span className="dash-cap-swatch-lbl">{s.label}</span>
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                  </div>
                );
              })()}

            </div>{/* end dash-row-2 */}

          </div>{/* end dash-hero-bento-group */}
        </div>{/* end dash-locked-wrap */}


        {/* ── Row 3: Allocation | Positions | Blog ── */}
        {/* DOM order = mobile order: allocation → positions → blog */}
        {/* Desktop: CSS grid-template-areas repositions to: positions left, allocation+blog right */}
        <div className={`dash-row-3${isDemo ? ' dash-locked-content' : ''}`}>

          {/* Allocation card */}
          <div className="dash-bento-invested">
            {(() => {
              const activePos = positions.filter(p => p.status !== 'closed');
              const activeTotal = activePos.reduce((s, p) => s + p.value, 0);
              const maxIndividual = 5;
              const hasOthers = activePos.length > maxIndividual;
              const slots = hasOthers ? activePos.slice(0, maxIndividual) : activePos;
              const tokenColors = slots.map((p, i) => {
                const tok = positionToken(p).toUpperCase();
                if (/BG12/.test(tok)) return '#F97316';
                if (/UG12/.test(tok)) return '#38BDF8';
                return STRATEGY_COLORS[i % STRATEGY_COLORS.length];
              });

              return (
                <>
                  {/* Top: title + legend */}
                  <div className="dash-alloc-top">
                    <span className="dash-bento-card-title">{t('allocation')}</span>

                    {!isDemo && activePos.length > 0 ? (
                      <div className="dash-alloc-legend">
                        {slots.map((p, i) => {
                          const pct = activeTotal > 0 ? (p.value / activeTotal * 100) : 0;
                          return (
                            <div key={p.vaultId}
                              className={`dash-alloc-legend-item${hoveredDonutIdx === i ? ' active' : ''}`}
                              onMouseEnter={() => setHoveredDonutIdx(i)}
                              onMouseLeave={() => setHoveredDonutIdx(null)}
                              onClick={() => setHoveredDonutIdx(prev => prev === i ? null : i)}>
                              <span className="dash-alloc-legend-dot" style={{ background: tokenColors[i] }} />
                              <span className="dash-alloc-legend-name">{positionToken(p)}</span>
                              <span className="dash-alloc-legend-pct" style={{ color: tokenColors[i] }}>{pct.toFixed(0)}%</span>
                            </div>
                          );
                        })}
                        {hasOthers && (
                          <div className="dash-alloc-legend-item">
                            <span className="dash-alloc-legend-dot" style={{ background: '#666' }} />
                            <span className="dash-alloc-legend-name">Others</span>
                          </div>
                        )}
                      </div>
                    ) : isDemo ? (
                      <div className="dash-alloc-legend">
                        {['BTC', 'ETH', 'USDC'].map(name => (
                          <div key={name} className="dash-alloc-legend-item dash-swatch-ghost">
                            <span className="dash-alloc-legend-dot" style={{ background: getTokenColor(name) }} />
                            <span className="dash-alloc-legend-name">{name}</span>
                          </div>
                        ))}
                      </div>
                    ) : null}
                  </div>

                  {/* Bottom: half-donut anchored to card edge */}
                  <div className="dash-alloc-half-donut">
                    <HalfDonutChart
                      positions={isDemo ? [] : activePos}
                      onHover={setHoveredDonutIdx}
                      placeholder={isDemo || activePos.length === 0}
                      colors={tokenColors}
                      chartRef={halfDonutRef}
                      fullDonut={isMobile}
                      hoveredIdx={hoveredDonutIdx}
                    />
                  </div>
                </>
              );
            })()}
          </div>

          {/* Positions panel */}
          <div className="dash-positions-panel">
            <div className="dash-positions-panel-header">
              <span className="dash-bento-card-title">{t('yourPositions')}</span>
            </div>

            {!isDemo && positions.length > 0 ? (
              <>
                {/* Column headers — once, direct grid children */}
                <div className="dash-pos-thead">
                  <span>Vault</span>
                  <span>{t('shares')}</span>
                  <span>{t('value')}</span>
                  <span>{t('avgEntry')}</span>
                  <span>{t('invested')}</span>
                  <span>{t('withdrawn')}</span>
                  <span>{t('pnlNet')}</span>
                  <span />
                </div>

                {/* Desktop table */}
                <div className="dash-pos-rows">
                  {pagedPositions.map((pos) => (
                    <div key={pos.vaultId} className="dash-pos-row">
                      <div className="dash-pos-row-identity">
                        <img src={pos.providerLogo} alt="" className="dash-pos-row-logo" />
                        <span className="dash-pos-row-name">{pos.name}</span>
                        <span className={`dash-pos-row-badge dash-pos-row-badge--${
                          pos.status === 'closed' ? 'closed'
                          : (pos.pnl ?? 0) >= 0 ? 'active-pos'
                          : 'active-neg'
                        }`}>
                          <span className="dash-pos-badge-dot" />
                        </span>
                      </div>
                      <div className="dash-pos-row-cols">
                        <span className="dash-pos-col-val">{pos.shares != null ? pos.shares.toFixed(4) : '—'}</span>
                        <span className="dash-pos-col-val">{fmtUsd(pos.value)}</span>
                        <span className="dash-pos-col-val">{pos.avgEntry != null ? fmtUsd(pos.avgEntry) : '—'}</span>
                        <span className="dash-pos-col-val">{fmtUsd(pos.costBasis ?? 0)}</span>
                        <span className="dash-pos-col-val">{fmtUsd(pos.totalWithdrawn ?? 0)}</span>
                        <span className={`dash-pos-col-val dash-pos-col-pnl${pos.pnl != null ? (pos.pnl >= 0 ? ' positive' : ' negative') : ''}`}>
                          {pos.pnl != null ? `${pos.pnl >= 0 ? '+' : ''}${fmtUsd(pos.pnl)}` : '—'}
                        </span>
                        <div className="dash-pos-row-actions">
                          <button className="dash-pos-row-btn" title={t('openPosition')} onClick={() => navigate(`/vault/${pos.vaultId}`)}>
                            <i className="ph-bold ph-arrow-square-out" />
                          </button>
                          <button className="dash-pos-row-btn" title={t('transactions')} onClick={() => navigate(`/transactions?vault=${pos.vaultId}`)}>
                            <i className="ph-bold ph-clock-counter-clockwise" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Mobile accordion */}
                <div className="dash-pos-mobile">
                  {pagedPositions.map((pos, i) => {
                    const isOpen = openPositionIdx === i;
                    const badgeCls = pos.status === 'closed' ? 'closed' : (pos.pnl ?? 0) >= 0 ? 'active-pos' : 'active-neg';
                    return (
                      <div key={pos.vaultId} className={`dash-pos-mobile-row${isOpen ? ' open' : ''}`}>
                        <button className="dash-pos-mobile-summary" onClick={() => setOpenPositionIdx(isOpen ? null : i)}>
                          <div className="dash-pos-mobile-identity">
                            <img src={pos.providerLogo} alt="" className="dash-pos-row-logo" />
                            <span className="dash-pos-row-name">{pos.name}</span>
                            <span className={`dash-pos-row-badge dash-pos-row-badge--${badgeCls}`}>
                              <span className="dash-pos-badge-dot" />
                            </span>
                          </div>
                          <div className="dash-pos-mobile-right">
                            <span className="dash-pos-mobile-val">{fmtUsd(pos.value)}</span>
                            <span className={`dash-pos-mobile-pnl${pos.pnl != null ? (pos.pnl >= 0 ? ' positive' : ' negative') : ''}`}>
                              {pos.pnl != null ? `${pos.pnl >= 0 ? '+' : ''}${fmtUsd(pos.pnl)}` : '—'}
                            </span>
                          </div>
                          <i className="ph-bold ph-caret-down dash-pos-mobile-chevron" />
                        </button>
                        {isOpen && (
                          <div className="dash-pos-mobile-detail">
                            <div className="dash-pos-mobile-stats">
                              <div className="dash-pos-mobile-stat">
                                <span className="dash-pos-mobile-stat-lbl">{t('shares')}</span>
                                <span className="dash-pos-mobile-stat-val">{pos.shares != null ? pos.shares.toFixed(4) : '—'}</span>
                              </div>
                              <div className="dash-pos-mobile-stat">
                                <span className="dash-pos-mobile-stat-lbl">{t('avgEntry')}</span>
                                <span className="dash-pos-mobile-stat-val">{pos.avgEntry != null ? fmtUsd(pos.avgEntry) : '—'}</span>
                              </div>
                              <div className="dash-pos-mobile-stat">
                                <span className="dash-pos-mobile-stat-lbl">{t('invested')}</span>
                                <span className="dash-pos-mobile-stat-val">{fmtUsd(pos.costBasis ?? 0)}</span>
                              </div>
                              <div className="dash-pos-mobile-stat">
                                <span className="dash-pos-mobile-stat-lbl">{t('withdrawn')}</span>
                                <span className="dash-pos-mobile-stat-val">{fmtUsd(pos.totalWithdrawn ?? 0)}</span>
                              </div>
                            </div>
                            <div className="dash-pos-mobile-actions">
                              <button className="dash-pos-row-btn" onClick={() => navigate(`/vault/${pos.vaultId}`)}>
                                <i className="ph-bold ph-arrow-square-out" />
                              </button>
                              <button className="dash-pos-row-btn" onClick={() => navigate(`/transactions?vault=${pos.vaultId}`)}>
                                <i className="ph-bold ph-clock-counter-clockwise" />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {(() => {
                  if (totalPages < 1) return null;
                  const page = positionPage;
                  const visible = new Set([0, totalPages - 1, page, page - 1, page + 1].filter(p => p >= 0 && p < totalPages));
                  const sorted = [...visible].sort((a, b) => a - b);
                  const items = [];
                  let prev = -1;
                  for (const p of sorted) {
                    if (p - prev > 1) items.push('…');
                    items.push(p);
                    prev = p;
                  }
                  return (
                    <div className="dash-pos-panel-footer">
                      <button className="strat-page-btn strat-page-nav" disabled={page === 0} onClick={() => setPositionPage(p => p - 1)}>‹</button>
                      {items.map((item, i) =>
                        item === '…'
                          ? <span key={`e${i}`} className="strat-page-ellipsis">…</span>
                          : <button key={item} className={`strat-page-btn ${item === page ? 'active' : ''}`} onClick={() => setPositionPage(item)}>{item + 1}</button>
                      )}
                      <button className="strat-page-btn strat-page-nav" disabled={page >= totalPages - 1} onClick={() => setPositionPage(p => p + 1)}>›</button>
                    </div>
                  );
                })()}
              </>
            ) : (
              <div className="dash-empty" style={{ border: 'none', flex: 1 }}>
                {!isDemo && <i className="ph-bold ph-chart-pie-slice" style={{ fontSize: 40, color: 'var(--text-2)' }} />}
                {!isDemo && <h3>{t('noPositions')}</h3>}
                {!isDemo && <p>{t('noPositionsSub')}</p>}
                {!isDemo && (
                  <button className="dash-empty-btn" onClick={() => navigate('/strategies')}>
                    <i className="ph-bold ph-arrow-right" /> {t('browseStrategies')}
                  </button>
                )}
              </div>
            )}
          </div>

        </div>{/* end dash-row-3 */}

      </div>
    </AppLayout>
  );
}
